import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Check, Minus, Plus, CreditCard, Users, Loader2, ArrowLeft, ArrowRight,
  Shield, Zap, HeadphonesIcon, Star, Clock, TrendingUp, Lock, BarChart3, AlertTriangle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BASE_PRICE = 49.0;
const INCLUDED_USERS = 1;
const EXTRA_USER_PRICE = 10.0;

const FEATURES = [
  "Pipeline Kanban ilimitado",
  "Campos personalizados ilimitados",
  "Importação de dados (Excel, CSV)",
  "Formulários públicos de captação",
  "Automações e workflows",
  "Relatórios e forecast completos",
  "Integração WhatsApp nativa",
  "Resumos com IA (OpenAI)",
  "SLA inteligente com alertas",
  "Suporte prioritário por email",
];

const TESTIMONIALS = [];

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [extraUsers, setExtraUsers] = useState(0);
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [pricing, setPricing] = useState({ base_price: 49, included_users: 1, extra_user_price: 10 });
  const [pageLoading, setPageLoading] = useState(true);

  const totalUsers = pricing.included_users + extraUsers;
  const total = pricing.base_price + extraUsers * pricing.extra_user_price;

  useEffect(() => {
    axios.get(`${API}/stripe/my-subscription`, { withCredentials: true })
      .then(res => {
        setSubscription(res.data);
        if (res.data?.pricing) setPricing(res.data.pricing);
      })
      .catch(() => {})
      .finally(() => setPageLoading(false));
  }, []);

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/stripe/checkout`, {
        extra_users: extraUsers,
        origin_url: window.location.origin,
      }, { withCredentials: true });

      if (res.data.upgraded) {
        const changeType = totalUsers < currentUsers ? "downgrade" : "upgrade";
        navigate("/checkout/sucesso?upgraded=true&change=" + changeType + "&users=" + res.data.total_users + "&amount=" + res.data.amount);
      } else if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const isActive = subscription?.subscription_status === "active";
  const currentPlan = subscription?.subscription_plan;
  const currentUsers = currentPlan?.total_users || 0;
  const activeUsers = subscription?.active_users || 0;
  const isDowngrade = isActive && totalUsers < currentUsers;
  const blockedDowngrade = isDowngrade && activeUsers > totalUsers;

  if (pageLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  if (user?.role !== "admin") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 flex items-center justify-center">
        <div className="text-center">
          <Lock className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">Somente administradores podem gerenciar a assinatura.</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>Voltar</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors" data-testid="checkout-back-btn">
            <ArrowLeft className="w-4 h-4" />Voltar
          </button>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <Shield className="w-4 h-4 text-emerald-500" />
              <span className="text-xs text-slate-500">Pagamento 100% seguro</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Lock className="w-4 h-4 text-slate-400" />
              <span className="text-xs text-slate-500">Criptografia SSL</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-10">
        <div className="text-center mb-10">
          <Badge className="bg-blue-50 text-blue-600 border-blue-200 text-xs mb-3 px-3 py-1">{isActive ? "Alterar plano" : "Oferta especial"}</Badge>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">{isActive ? "Altere seu plano" : "Comece a fechar mais negócios hoje"}</h1>
          <p className="text-slate-500 mt-2 text-base max-w-lg mx-auto">{isActive ? `Plano atual: ${currentUsers} usuário(s) por R$${currentPlan?.amount?.toFixed(2)}/mês. Ajuste a quantidade abaixo.` : "Junte-se a centenas de equipes que já usam o Pipe4.me para vender mais e melhor."}</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 items-start">
          {/* Plan card */}
          <div className="lg:col-span-3 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-6" style={{ background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)" }}>
              <div className="flex items-center justify-between text-white">
                <div>
                  <h2 className="text-lg font-bold">Pipe4.me</h2>
                  <p className="text-blue-200 text-sm mt-0.5">CRM completo para sua equipe de vendas</p>
                </div>
                <Badge className="bg-white/20 text-white border-0 text-xs">Mensal</Badge>
              </div>
              {/* Urgency strip */}
              <div className="mt-3 flex items-center gap-2 bg-white/10 rounded-lg px-3 py-2">
                <Clock className="w-3.5 h-3.5 text-yellow-300" />
                <span className="text-xs text-blue-100 font-medium">Cancele quando quiser. Sem fidelidade, sem multa.</span>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Pricing breakdown */}
              <div className="space-y-3">
                <div className="flex items-center justify-between py-3 border-b border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                      <Users className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Plano base</p>
                      <p className="text-xs text-slate-400">{pricing.included_users} usuário{pricing.included_users > 1 ? "s" : ""} incluso{pricing.included_users > 1 ? "s" : ""}</p>
                    </div>
                  </div>
                  <span className="text-sm font-bold text-slate-900">R${pricing.base_price.toFixed(2)}/mês</span>
                </div>

                {/* Extra users selector */}
                <div className="flex items-center justify-between py-3 border-b border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                      <Users className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Usuários adicionais</p>
                      <p className="text-xs text-slate-400">R${pricing.extra_user_price.toFixed(2)}/usuário/mês</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid="decrease-users-btn"
                      onClick={() => setExtraUsers(Math.max(0, extraUsers - 1))}
                      disabled={extraUsers === 0}
                      className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-50 disabled:opacity-30 transition-colors"
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span data-testid="extra-users-count" className="w-8 text-center text-sm font-bold text-slate-900">{extraUsers}</span>
                    <button
                      data-testid="increase-users-btn"
                      onClick={() => setExtraUsers(extraUsers + 1)}
                      className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-50 transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Total */}
                <div className="flex items-center justify-between pt-2">
                  <div>
                    <p className="text-base font-bold text-slate-900">Total mensal</p>
                    <p className="text-xs text-slate-400">{totalUsers} usuário(s)</p>
                  </div>
                  <div className="text-right">
                    <span data-testid="checkout-total" className="text-2xl font-bold text-slate-900">R${total.toFixed(2)}</span>
                    <span className="text-sm text-slate-400 ml-1">/mês</span>
                  </div>
                </div>
              </div>

              {/* Downgrade blocked warning */}
              {blockedDowngrade && (
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-rose-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-rose-700">Não é possível reduzir para {totalUsers} usuário(s)</p>
                    <p className="text-xs text-rose-600 mt-1">
                      Você possui {activeUsers} usuário(s) ativo(s). Remova {activeUsers - totalUsers} usuário(s) em <strong>Configurações &gt; Usuários</strong> antes de fazer o downgrade.
                    </p>
                  </div>
                </div>
              )}

              {/* CTA */}
              <Button
                data-testid="checkout-pay-btn"
                onClick={handleCheckout}
                disabled={loading || blockedDowngrade}
                className="w-full h-12 text-white font-semibold text-base rounded-xl transition-all hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: "#3B82F6" }}
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    {isActive
                      ? (totalUsers > currentUsers ? `Fazer upgrade — R$${total.toFixed(2)}/mês` : totalUsers < currentUsers ? `Fazer downgrade — R$${total.toFixed(2)}/mês` : `Manter plano — R$${total.toFixed(2)}/mês`)
                      : `Assinar agora — R$${total.toFixed(2)}/mês`}
                  </>
                )}
              </Button>

              {/* Trust signals */}
              <div className="flex items-center justify-center gap-6 pt-1">
                <div className="flex items-center gap-1.5 text-slate-400">
                  <Shield className="w-3.5 h-3.5 text-emerald-500" />
                  <span className="text-[11px]">Stripe Checkout</span>
                </div>
                <div className="flex items-center gap-1.5 text-slate-400">
                  <Lock className="w-3.5 h-3.5" />
                  <span className="text-[11px]">Dados criptografados</span>
                </div>
                <div className="flex items-center gap-1.5 text-slate-400">
                  <Clock className="w-3.5 h-3.5" />
                  <span className="text-[11px]">Cancele quando quiser</span>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-2 space-y-5">
            {/* Features */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
              <h3 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-500" />Tudo incluso no plano
              </h3>
              <ul className="space-y-2.5">
                {FEATURES.map((f, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm text-slate-600">
                    <Check className="w-4 h-4 text-blue-500 flex-shrink-0" />{f}
                  </li>
                ))}
              </ul>
            </div>

            {/* Comparison nudge */}
            <div className="bg-blue-50 rounded-2xl border border-blue-100 p-5">
              <h4 className="text-sm font-bold text-blue-900 flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-blue-600" />Comparado ao mercado
              </h4>
              <div className="space-y-1.5">
                <div className="flex justify-between text-xs">
                  <span className="text-blue-700">Pipe4.me</span>
                  <span className="font-bold text-blue-900">R$49/mês</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500">Pipedrive</span>
                  <span className="text-slate-500">~R$59/mês</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500">HubSpot Sales</span>
                  <span className="text-slate-500">~R$90/mês</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500">Salesforce</span>
                  <span className="text-slate-500">~R$150/mês</span>
                </div>
              </div>
            </div>

            {/* Help */}
            <div className="bg-slate-50 rounded-2xl border border-slate-200 p-5">
              <div className="flex items-center gap-2.5 mb-2">
                <HeadphonesIcon className="w-4 h-4 text-slate-400" />
                <h4 className="text-sm font-bold text-slate-700">Precisa de ajuda?</h4>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Fale com nossa equipe para tirar dúvidas sobre planos e funcionalidades.
              </p>
              <a href="mailto:contato@pipe4.me" className="text-xs font-medium text-blue-600 hover:underline">
                contato@pipe4.me
              </a>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
